import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Camera, ImageUp, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useDeferredValue, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import { customerSchema } from '@/lib/schemas';
import { formatCurrency } from '@/lib/utils';
import { fetchPriceList, PRICE_LIST_QUERY_KEY, type PriceListItem } from '@/features/price-list/price-list-api';
import {
  COMPONENTS,
  UNIT_KINDS,
  carpentryApi,
  type Component,
  type DrawingUnit,
} from '@/features/processes/processes-api';
import { quoteSchema, type Quote } from './quotes-api';

/* ---------------------------------------------------------------------------
   הצעה משרטוט (נגרות): מצלמים סקיצה → השרת מזהה יחידות ומידות → המידות
   ניתנות לתיקון → החשבון (לוחות, קנט, פרזול, עבודה) מחושב בשרת מהמחירון →
   הצעה רגילה עם כמויות.
   --------------------------------------------------------------------------- */

const COMPONENT_CATEGORY: Record<Component, PriceListItem['category']> = {
  material: 'MATERIAL',
  edgeBand: 'MATERIAL',
  hinge: 'HARDWARE',
  slide: 'HARDWARE',
  handle: 'HARDWARE',
  labor: 'SERVICE',
};

const AUTO = '__auto__';
const MAX_EDGE_PX = 2000;

const blankUnit = (): DrawingUnit => ({
  kind: 'cabinet',
  label: '',
  widthCm: 100,
  heightCm: 80,
  depthCm: 56,
  qty: 1,
  doors: 2,
  drawers: 0,
  shelves: 1,
});

/**
 * מקטינים לפני ההעלאה: תמונת טלפון של 12MP היא 4–8MB, והמודל לא רואה
 * יותר פרטים מעל ~2000px. גם ממיר HEIC ל-JPEG בדפדפנים שמפענחים אותו.
 */
async function downscale(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && (file.type === 'image/jpeg' || file.type === 'image/png') && file.size < 4_000_000) {
      return file;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    return blob ? new File([blob], 'drawing.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function DrawingQuoteDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (q: Quote) => void }) {
  const { t } = useTranslation(['quotes', 'processes', 'common']);
  const toast = useToast();
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const term = useDeferredValue(search.trim());
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [hint, setHint] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [units, setUnits] = useState<DrawingUnit[]>([]);
  const [unclear, setUnclear] = useState<string[]>([]);
  const [demo, setDemo] = useState(false);
  const [codes, setCodes] = useState<Partial<Record<Component, string | null>>>({});
  const [notes, setNotes] = useState('');

  const customers = useQuery({
    queryKey: ['customer-search', term],
    queryFn: ({ signal }) =>
      request(`/customers/search?q=${encodeURIComponent(term)}`, { schema: z.array(customerSchema), signal }),
    enabled: term.length >= 2 && !customer,
  });
  const prices = useQuery({
    queryKey: [PRICE_LIST_QUERY_KEY, false],
    queryFn: ({ signal }) => fetchPriceList(false, signal),
  });

  const analyze = useMutation({
    mutationFn: async (file: File) => carpentryApi.analyze(await downscale(file), hint.trim() || undefined),
    onSuccess: ({ analysis }) => {
      setUnits(analysis.units.length > 0 ? analysis.units : [blankUnit()]);
      setUnclear(analysis.unclear);
      setDemo(analysis.fake === true);
      if (analysis.units.length === 0) toast.error(t('drawing.noUnits'), t('drawing.noUnitsDetail'));
    },
    onError: (e) => toast.error(t('drawing.analyzeFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    analyze.mutate(file);
  };

  // החישוב בשרת — אותו מחשבון שנבדק, לא העתק בדפדפן. debounce כדי
  // שהקלדת מידה לא תשלח בקשה על כל ספרה.
  const pricingInput = useDebounced(JSON.stringify({ units, codes }), 350);
  const draft = useQuery({
    queryKey: ['carpentry-price', pricingInput],
    queryFn: () => {
      const input = JSON.parse(pricingInput) as { units: DrawingUnit[]; codes: typeof codes };
      return carpentryApi.price(input.units, input.codes);
    },
    enabled: units.length > 0,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const submission = useRef<{ payload: string; key: string } | null>(null);
  const create = useMutation({
    mutationFn: () => {
      const summary = units
        .map((u) => `${u.label || t(`processes:unitKinds.${u.kind}`)} ${u.widthCm}×${u.heightCm}×${u.depthCm}${u.qty > 1 ? ` (×${u.qty})` : ''}`)
        .join(', ');
      const body = {
        customerId: customer!.id,
        lines: draft.data!.lines.map((l) => ({ priceCode: l.priceCode, qty: l.qty })),
        notes: [notes.trim(), t('drawing.notesPrefix', { summary })].filter(Boolean).join('\n').slice(0, 1000),
      };
      const payload = JSON.stringify(body);
      if (submission.current?.payload !== payload) submission.current = { payload, key: crypto.randomUUID() };
      return request('/quotes', { method: 'POST', body, schema: quoteSchema, idempotencyKey: submission.current.key });
    },
    onSuccess: onCreated,
    onError: (e) => toast.error(t('toast.createFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const setUnit = (i: number, patch: Partial<DrawingUnit>) =>
    setUnits((prev) => prev.map((u, j) => (j === i ? { ...u, ...patch } : u)));
  const COUNTS: ReadonlyArray<keyof DrawingUnit> = ['qty', 'doors', 'drawers', 'shelves'];
  const numeric = (i: number, key: keyof DrawingUnit, min: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    // ספירות שלמות; ערך מעל התקרה נשלח כמו שהוא והשרת מחזיר שגיאה מוצגת.
    if (Number.isFinite(n) && e.target.value !== '') {
      setUnit(i, { [key]: Math.max(min, COUNTS.includes(key) ? Math.round(n) : n) });
    }
  };

  // התמחור חייב להיות של המידות *הנוכחיות*: לא בזמן ה-debounce, לא נתון
  // קודם שנשמר (keepPreviousData) ולא אחרי שגיאה — אחרת ההצעה נוצרת
  // מהשורות הישנות בזמן שההערות מתארות את המידות החדשות.
  const pricingFresh =
    pricingInput === JSON.stringify({ units, codes }) && !draft.isPlaceholderData && !draft.isError;
  const canCreate =
    !!customer && pricingFresh && !!draft.data && draft.data.lines.length > 0 && !draft.isFetching;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl" aria-describedby="drawing-quote-desc">
        <DialogHeader>
          <DialogTitle>{t('drawing.title')}</DialogTitle>
          <DialogDescription id="drawing-quote-desc">
            {t('drawing.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          {customer ? (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <span className="text-sm font-medium text-fg">{customer.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setCustomer(null)}>
                {t('form.change')}
              </Button>
            </div>
          ) : (
            <div>
              <Field label={t('form.customer')} htmlFor="dq-customer" required hint={t('form.customerHint')}>
                <Input id="dq-customer" autoFocus value={search} onChange={(e) => setSearch(e.target.value)} />
              </Field>
              {customers.data && customers.data.length > 0 ? (
                <ul className="mt-1 max-h-40 overflow-auto rounded-lg border border-border">
                  {customers.data.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                        onClick={() => setCustomer({ id: c.id, name: c.name })}
                      >
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}

          <section className="space-y-3 rounded-(--radius-md) border border-border p-3">
            <div className="flex flex-wrap items-start gap-3">
              {preview ? (
                <img src={preview} alt={t('drawing.previewAlt')} className="h-28 w-28 rounded-(--radius-md) border border-border object-cover" />
              ) : null}
              <div className="flex min-w-48 flex-1 flex-col gap-2">
                <Textarea
                  rows={2}
                  maxLength={500}
                  placeholder={t('drawing.hintPlaceholder')}
                  aria-label={t('drawing.hintAria')}
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" loading={analyze.isPending} onClick={() => cameraInput.current?.click()}>
                    <Camera aria-hidden />
                    {t('drawing.camera')}
                  </Button>
                  <Button size="sm" variant="secondary" disabled={analyze.isPending} onClick={() => galleryInput.current?.click()}>
                    <ImageUp aria-hidden />
                    {t('drawing.gallery')}
                  </Button>
                  {units.length === 0 ? (
                    <Button size="sm" variant="ghost" onClick={() => setUnits([blankUnit()])}>
                      {t('drawing.manual')}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
            {/* capture=environment פותח ישר את המצלמה האחורית בטלפון. */}
            <input
              ref={cameraInput}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <input
              ref={galleryInput}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              className="hidden"
              onChange={(e) => {
                onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            {analyze.isPending ? (
              <p className="flex items-center gap-2 text-xs text-fg-muted">
                <Sparkles className="size-3.5 animate-pulse text-accent" aria-hidden />
                {t('drawing.reading')}
              </p>
            ) : null}
            {demo ? (
              <Badge tone="warning" dot={false}>
                {t('drawing.demo')}
              </Badge>
            ) : null}
            {unclear.length > 0 ? (
              <ul className="space-y-1 rounded-(--radius-md) bg-warning-subtle p-2 text-xs text-warning">
                {unclear.map((u) => (
                  <li key={u} className="flex gap-1.5">
                    <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
                    {u}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          {units.length > 0 ? (
            <section className="space-y-2">
              <h3 className="text-sm font-medium text-fg">{t('drawing.unitsTitle')}</h3>
              <ul className="space-y-2">
                {units.map((u, i) => (
                  <li key={i} className="space-y-2 rounded-(--radius-md) border border-border p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={u.kind} onValueChange={(kind) => setUnit(i, { kind: kind as DrawingUnit['kind'] })}>
                        <SelectTrigger className="w-36" aria-label={t('drawing.unitKindAria')}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_KINDS.map((k) => (
                            <SelectItem key={k} value={k}>
                              {t(`processes:unitKinds.${k}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        className="min-w-32 flex-1"
                        placeholder={t('drawing.unitNamePlaceholder')}
                        aria-label={t('drawing.unitNameAria')}
                        value={u.label ?? ''}
                        onChange={(e) => setUnit(i, { label: e.target.value })}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('drawing.removeUnit')}
                        onClick={() => setUnits((prev) => prev.filter((_, j) => j !== i))}
                      >
                        <Trash2 aria-hidden />
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                      {(
                        [
                          ['widthCm', 5],
                          ['heightCm', 1],
                          ['depthCm', 1],
                          ['qty', 1],
                          ['doors', 0],
                          ['drawers', 0],
                          ['shelves', 0],
                        ] as const
                      ).map(([key, min]) => (
                        <label key={key} className="flex flex-col gap-1 text-2xs text-fg-muted">
                          {t(`drawing.dims.${key}`)}
                          <Input
                            type="number"
                            dir="ltr"
                            inputMode="numeric"
                            min={min}
                            value={u[key]}
                            onChange={numeric(i, key, min)}
                          />
                        </label>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
              <Button size="sm" variant="secondary" onClick={() => setUnits((prev) => [...prev, blankUnit()])}>
                <Plus aria-hidden />
                {t('drawing.addUnit')}
              </Button>
            </section>
          ) : null}

          {units.length > 0 ? (
            <section className="space-y-2">
              <h3 className="text-sm font-medium text-fg">{t('drawing.materialsTitle')}</h3>
              <div className="grid gap-2 sm:grid-cols-3">
                {COMPONENTS.map((c) => {
                  const options = (prices.data ?? []).filter((p) => p.category === COMPONENT_CATEGORY[c]);
                  const auto = draft.data?.codes[c];
                  return (
                    <Field key={c} label={t(`drawing.components.${c}`)} htmlFor={`dq-${c}`}>
                      <Select
                        value={codes[c] ?? AUTO}
                        onValueChange={(v) => setCodes((prev) => ({ ...prev, [c]: v === AUTO ? undefined : v }))}
                      >
                        <SelectTrigger id={`dq-${c}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={AUTO}>
                            {auto && !codes[c]
                              ? t('drawing.autoWith', { item: options.find((o) => o.code === auto)?.description ?? auto })
                              : t('drawing.auto')}
                          </SelectItem>
                          {options.map((o) => (
                            <SelectItem key={o.code} value={o.code}>
                              {o.description} · {formatCurrency(o.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  );
                })}
              </div>
            </section>
          ) : null}

          {units.length > 0 ? (
            <section className="space-y-2 border-t border-border pt-3">
              {draft.isPending ? <Skeleton className="h-24 w-full" /> : null}
              {draft.isError ? (
                <p className="text-sm text-danger">
                  {draft.error instanceof ApiError ? draft.error.message : t('drawing.calcFailed')}
                </p>
              ) : null}
              {draft.data && !draft.isError ? (
                <>
                  <p className="text-2xs text-fg-subtle">
                    {t('drawing.calcSummary', {
                      area: draft.data.calc.areaM2,
                      sheets: draft.data.calc.sheets,
                      edge: draft.data.calc.edgeBandM,
                      hours: draft.data.calc.laborHours,
                    })}
                  </p>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-border">
                      {draft.data.lines.map((l) => (
                        <tr key={l.priceCode}>
                          <td className="py-1.5 text-fg">{l.description}</td>
                          <td className="tabular py-1.5 text-center text-fg-muted">
                            {l.qty} × {formatCurrency(l.unitPrice)}
                          </td>
                          <td className="tabular py-1.5 text-end font-medium text-fg">{formatCurrency(l.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {draft.data.missing.length > 0 ? (
                    <p className="flex gap-1.5 text-xs text-warning">
                      <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
                      {t('drawing.missing', { items: draft.data.missing.map((m) => t(`drawing.components.${m}`)).join(', ') })}
                    </p>
                  ) : null}
                  <p className="flex justify-between border-t border-border pt-2 text-sm">
                    <span className="text-fg-muted">{t('form.total')}</span>
                    <span className="tabular font-semibold text-fg">{formatCurrency(draft.data.total)}</span>
                  </p>
                </>
              ) : null}
              <Field label={t('form.notes')} htmlFor="dq-notes">
                <Textarea id="dq-notes" rows={2} maxLength={600} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            </section>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!canCreate} loading={create.isPending} onClick={() => create.mutate()}>
            {t('form.createAndSend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
